import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ManualInvoiceForm = () => {
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [lineItems, setLineItems] = useState([{ description: '', quantity: 1, rate: 0, amount: 0 }]);
  const [taxRate, setTaxRate] = useState(18);
  const [notes, setNotes] = useState('');

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', quantity: 1, rate: 0, amount: 0 }]);
  };

  const removeLineItem = (index) => {
    setLineItems(lineItems?.filter((_, i) => i !== index));
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    if (field === 'quantity' || field === 'rate') {
      updated[index].amount = updated?.[index]?.quantity * updated?.[index]?.rate;
    }
    setLineItems(updated);
  };

  const subtotal = lineItems?.reduce((sum, item) => sum + item?.amount, 0);
  const taxAmount = (subtotal * taxRate) / 100;
  const total = subtotal + taxAmount;

  const handleGenerate = () => {
    console.log('Generating manual invoice:', {
      customerName,
      customerEmail,
      lineItems,
      taxRate,
      notes,
      subtotal,
      taxAmount,
      total
    });
  };

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
          <Icon name="FileText" size={20} className="text-primary" />
          Create Manual Invoice
        </h3>

        {/* Customer Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Customer Name *
            </label>
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e?.target?.value)}
              placeholder="Enter customer name"
              className="w-full px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Customer Email *
            </label>
            <input
              type="email"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e?.target?.value)}
              placeholder="customer@example.com"
              className="w-full px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
        </div>

        {/* Line Items */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <label className="block text-sm font-medium text-foreground">
              Line Items
            </label>
            <Button
              variant="outline"
              size="sm"
              onClick={addLineItem}
              iconName="Plus"
              iconPosition="left"
            >
              Add Item
            </Button>
          </div>

          <div className="space-y-3">
            {lineItems?.map((item, index) => (
              <div key={index} className="grid grid-cols-12 gap-3 items-start">
                <div className="col-span-12 md:col-span-5">
                  <input
                    type="text"
                    value={item?.description}
                    onChange={(e) => updateLineItem(index, 'description', e?.target?.value)}
                    placeholder="Description"
                    className="w-full px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
                <div className="col-span-4 md:col-span-2">
                  <input
                    type="number"
                    value={item?.quantity}
                    onChange={(e) => updateLineItem(index, 'quantity', Number(e?.target?.value))}
                    placeholder="Qty"
                    min="1"
                    className="w-full px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
                <div className="col-span-4 md:col-span-2">
                  <input
                    type="number"
                    value={item?.rate}
                    onChange={(e) => updateLineItem(index, 'rate', Number(e?.target?.value))}
                    placeholder="Rate"
                    min="0"
                    className="w-full px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
                <div className="col-span-3 md:col-span-2">
                  <div className="px-4 py-2 bg-muted/30 border border-border rounded-md text-sm text-foreground font-semibold">
                    ₹{item?.amount?.toLocaleString()}
                  </div>
                </div>
                <div className="col-span-1">
                  {lineItems?.length > 1 && (
                    <button
                      onClick={() => removeLineItem(index)}
                      className="p-2 hover:bg-destructive/10 rounded-md transition-smooth"
                      title="Remove item"
                    >
                      <Icon name="Trash2" size={16} className="text-destructive" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tax Configuration */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-foreground mb-2">
            Tax Rate (%)
          </label>
          <input
            type="number"
            value={taxRate}
            onChange={(e) => setTaxRate(Number(e?.target?.value))}
            min="0"
            max="100"
            className="w-full md:w-48 px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>

        {/* Notes */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-foreground mb-2">
            Notes (Optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e?.target?.value)}
            rows={3}
            placeholder="Add any additional notes or payment instructions..."
            className="w-full px-4 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
          />
        </div>

        {/* Invoice Summary */}
        <div className="bg-background border border-border rounded-lg p-6">
          <h4 className="text-sm font-semibold text-foreground mb-4">Invoice Summary</h4>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Subtotal</span>
              <span className="text-sm font-semibold text-foreground">₹{subtotal?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Tax ({taxRate}%)</span>
              <span className="text-sm font-semibold text-foreground">₹{taxAmount?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-border">
              <span className="text-base font-bold text-foreground">Total</span>
              <span className="text-xl font-bold text-foreground">₹{total?.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <Button
            variant="outline"
            size="lg"
            fullWidth
          >
            Save as Draft
          </Button>
          <Button
            variant="default"
            size="lg"
            onClick={handleGenerate}
            disabled={!customerName || !customerEmail || lineItems?.length === 0}
            iconName="FileText"
            iconPosition="left"
            fullWidth
          >
            Generate Invoice
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ManualInvoiceForm;